import { type CSSProperties, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useImageLoaded } from '@/components/ui/hooks/use-image-loaded';
import { cn } from '@/lib/utils';

type ImageStageProps = {
  src?: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
  /** A small derivative that is already in the browser cache (typically
   *  `caches['300w']`, painted moments ago by the grid or thumbnail strip).
   *  Drawn blurred behind the full image so the stage is never an empty box. */
  placeholderSrc?: string;
  /** Stage-box classes. Must give the box a definite size — that reservation
   *  is the whole point (see the component doc). */
  className?: string;
  style?: CSSProperties;
  /** Extra classes for the full-size image, e.g. a phase-dependent blur. */
  imageClassName?: string;
  spinnerClassName?: string;
  /** Controlled load state, for hosts that cache it per URL across navigation
   *  (the asset viewer prefetches neighbours). Omit to track it internally. */
  loaded?: boolean;
  onLoaded?: (src: string) => void;
  /** Overlay content — nav buttons, badges, gradients — stacked above the image. */
  children?: ReactNode;
};

/**
 * A fixed-size box that an image loads *into*, rather than one the image
 * sizes as it arrives.
 *
 * Image-forward modals here are sized by their content, so an `<img>` with no
 * intrinsic size yet collapses the dialog and it visibly jumps when the image
 * decodes. The stage takes a definite size from its host (`className`/`style`),
 * contains the image inside it, and cross-fades a cached low-res placeholder
 * into the full image — so the dialog opens at its final size and stays there,
 * including across navigation between portrait and landscape photos.
 *
 * The tradeoff is deliberate: an image whose aspect ratio differs from the
 * reserved box letterboxes against the stage background instead of resizing
 * the dialog around itself.
 *
 * @category Layout
 */
const ImageStage = ({
  src,
  alt,
  srcSet,
  sizes,
  placeholderSrc,
  className,
  style,
  imageClassName,
  spinnerClassName,
  loaded,
  onLoaded,
  children
}: ImageStageProps) => {
  // The hook re-arms for an incoming image (so navigation cross-fades like a
  // first open) and recognises a cached one, which skips the fade entirely.
  const { loaded: selfLoaded, imageRef, markLoaded } = useImageLoaded(src);

  const isLoaded = loaded ?? selfLoaded;

  // An empty src never fires load/error in a real browser; guard anyway so a
  // synthetic event can't mark a missing image as loaded and hide the spinner.
  const handleLoaded = () => {
    if (!src) return;
    markLoaded();
    onLoaded?.(src);
  };

  return (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden',
        className
      )}
      style={style}
    >
      {placeholderSrc && (
        <img
          src={placeholderSrc}
          alt=""
          aria-hidden
          className={cn(
            'absolute inset-0 h-full w-full scale-105 object-contain blur-lg transition-opacity duration-300',
            isLoaded ? 'opacity-0' : 'opacity-100'
          )}
        />
      )}
      <img
        ref={imageRef}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        onLoad={handleLoaded}
        onError={handleLoaded}
        className={cn(
          'relative block max-h-full max-w-full object-contain transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0',
          imageClassName
        )}
      />
      {!isLoaded && (
        <div
          role="status"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <Loader2
            className={cn(
              'h-10 w-10 animate-spin',
              spinnerClassName ?? 'text-muted-foreground'
            )}
          />
          <span className="sr-only">Loading image…</span>
        </div>
      )}
      {children}
    </div>
  );
};

export default ImageStage;
